import { capabilitiesOf } from "./analyticsCapabilities";
import { peakOf, roundedShare } from "./analyticsSentences";

// Os destaques da visao geral (spec do redesign, R6): regras fixas sobre o resumo e as sugestoes que a
// pagina ja carregou, sem modelo e sem pedidos novos. Cada destaque diz o numero, a frase (a chave de
// traducao e os parametros, que o componente formata) e o separador onde se age. Os de atencao vem
// primeiro, e no maximo quatro. As pesquisas de campanha sem resultado e as sugestoes nunca usadas sao de
// atencao, como as celulas a ambar das faixas e a etiqueta da tabela que dizem o mesmo. Os limiares sao
// valores de partida, assumidos e nao medidos (spec, R6): afinam-se com dados reais de producao. Os pisos (`conversationsChangeBase`, `peakDayMinimum`) sao dos
// mesmos: um chat com poucas conversas nao acende destaques por acaso (plano, Review Focus 5). Os dois dos
// operadores so acendem num chat com operadores (capabilitiesOf): sem caixa de handoff ainda pode haver
// pedidos de operador, que nao tiveram para onde ir e por isso nao mostram os operadores (D3 do plano das
// capacidades). Os das campanhas nao precisam disso: sem campanhas nao ha pesquisas que cheguem ao limite.
export const LIMITS = {
  peakHourRequests: 5,
  campaignSearches: 20,
  campaignShareWithoutResult: 20,
  conversationsChange: 20,
  conversationsChangeBase: 30,
  peakDayRatio: 1.5,
  peakDayMinimum: 10,
};
export const MAX_HIGHLIGHTS = 4;

// Num periodo de um dia (as series vem por hora) o dia de pico diria "17h foi o dia com mais conversas" e
// a sugestao sem uso acenderia todas as manhas: nenhum dos dois acende (decidido pelo Tiago a 20-Out, D13
// do plano do Hoje). So os destaques; a celula ambar e a etiqueta "Nunca usada" do Conteudo ficam.
const isOneDay = (summary) => summary.series.granularity === "hour";

const item = (key, tone, tab, figure, message, params = {}) => ({ key, tone, tab, figure, message, params });

const noOperatorInHours = (summary) => {
  if (!capabilitiesOf(summary).operators) return null;
  const count = summary.endings.handoff_requests.no_operator_within_hours;
  if (count === 0) return null;
  const message = count === 1 ? "analyticsHighlight.noOperatorInHoursOne" : "analyticsHighlight.noOperatorInHours";
  return item("noOperatorInHours", "attention", "conversations", { type: "count", value: count }, message);
};

// So para quem esta no fuso em que o backend avalia o horario: as horas do grafico sao as de quem ve, e o
// horario as do backend (plano, D1).
const handoffPeakOutsideHours = (summary, viewerZone) => {
  if (!capabilitiesOf(summary).operators) return null;
  const hours = summary.business_hours;
  if (!hours || hours.tz !== viewerZone) return null;
  const peak = peakOf(summary.when.handoff_requests.by_hour);
  if (!peak || peak.value < LIMITS.peakHourRequests) return null;
  if (peak.index >= hours.start_hour && peak.index < hours.end_hour) return null;
  return item("handoffPeakOutsideHours", "attention", "conversations", { type: "hour", value: peak.index }, "analyticsHighlight.handoffPeakOutsideHours", { count: peak.value });
};

const campaignsWithoutResult = (summary) => {
  const searches = summary.assistant.campaign_searches;
  const without = searches.values.false || 0;
  const share = roundedShare(without, searches.total);
  if (searches.total < LIMITS.campaignSearches || share < LIMITS.campaignShareWithoutResult) return null;
  return item("campaignsWithoutResult", "attention", "assistant", { type: "percent", value: share }, "analyticsHighlight.campaignsWithoutResult", { count: without, total: searches.total });
};

// So as configuradas e medidas: uma sugestao acima do teto nao e "nunca usada" (D13 da entrega 6).
const unusedSuggestions = (summary, firstQuestions) => {
  if (!firstQuestions || isOneDay(summary)) return null;
  const unused = firstQuestions.suggestions.contexts
    .flatMap((context) => context.suggestions)
    .filter((suggestion) => suggestion.configured && suggestion.measured && suggestion.uses === 0);
  if (unused.length === 0) return null;
  if (unused.length === 1) {
    return item("unusedSuggestions", "attention", "content", { type: "count", value: 0 }, "analyticsHighlight.unusedSuggestion", { label: unused[0].label });
  }
  return item("unusedSuggestions", "attention", "content", { type: "count", value: unused.length }, "analyticsHighlight.unusedSuggestions");
};

// Contra um anterior que so os dados cobrem em parte nao ha variacao (analyticsPeriod, previousIsComplete).
const conversationsChange = (summary, previousComplete) => {
  const { value, previous } = summary.headline.conversations;
  if (!previousComplete || !previous || previous < LIMITS.conversationsChangeBase) return null;
  const change = Math.round(((value - previous) / previous) * 100);
  if (Math.abs(change) < LIMITS.conversationsChange) return null;
  return item("conversationsChange", "context", "overview", { type: "change", value: change }, "analyticsHighlight.conversationsChange");
};

const peakDay = (summary) => {
  if (isOneDay(summary)) return null;
  const values = summary.series.metrics.conversations.current;
  const peak = peakOf(values);
  if (!peak || peak.value < LIMITS.peakDayMinimum) return null;
  const mean = values.reduce((sum, value) => sum + value, 0) / values.length;
  if (peak.value < LIMITS.peakDayRatio * mean) return null;
  return item("peakDay", "context", "overview", { type: "count", value: peak.value }, "analyticsHighlight.peakDay", { day: summary.series.buckets[peak.index] });
};

export const highlights = ({ summary, firstQuestions, viewerZone, previousComplete }) =>
  [
    noOperatorInHours(summary),
    handoffPeakOutsideHours(summary, viewerZone),
    campaignsWithoutResult(summary),
    unusedSuggestions(summary, firstQuestions),
    conversationsChange(summary, previousComplete),
    peakDay(summary),
  ]
    .filter(Boolean)
    .slice(0, MAX_HIGHLIGHTS);
